import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, MessageCircle } from "lucide-react";
import { useAuth } from "@/dashboard/AuthContext";

const formatErr = (detail) => {
  if (!detail) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-surface flex items-center justify-center px-5 font-sans">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-brand-blue flex items-center justify-center mb-4">
            <MessageCircle className="w-7 h-7 text-white" strokeWidth={2} />
          </div>
          <h1 className="font-display font-bold text-2xl text-brand-ink">WhatsApp Dashboard</h1>
          <p className="text-brand-muted text-sm mt-1">SNV Elite Solutions · Internal Access</p>
        </div>

        <form
          onSubmit={submit}
          data-testid="login-form"
          className="bg-white rounded-2xl border border-brand-border p-7 card-soft"
        >
          <div className="mb-5">
            <label className="block text-xs font-display font-semibold uppercase tracking-wider text-brand-slate mb-2">
              Email
            </label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              placeholder="you@snvelite.com"
              className="w-full border border-brand-border rounded-lg px-4 py-3 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
            />
          </div>
          <div className="mb-6">
            <label className="block text-xs font-display font-semibold uppercase tracking-wider text-brand-slate mb-2">
              Password
            </label>
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              placeholder="••••••••"
              className="w-full border border-brand-border rounded-lg px-4 py-3 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white font-display font-semibold rounded-lg px-6 py-3.5 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <p className="text-center text-brand-muted text-xs mt-6">
          Authorized personnel only. All activity is logged.
        </p>
      </div>
    </div>
  );
};

export default Login;
